'use client';

// Development builds only: when a solve fails (the mock backend fails every
// one) or is refused for free optimizer runs, offer the dev account a refill.
//
// Mounted once by the root layout, and only in development builds. It still
// renders nothing unless the server grants this session the dev tools and the
// account is metered — see lib/devAccount.ts for both locks.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Button from '@/components/ui/Button';
import { useUpgradeModal } from '@/components/upgrade/UpgradeModalProvider';
import {
  DEV_COMPUTE_FAILED_EVENT,
  DEV_TOOLS_BUILD,
  resetDevCredits,
  type DevComputeFailure,
} from '@/lib/devAccount';
import { useWallet } from '@/lib/useWallet';

export default function DevComputeFailurePrompt() {
  const [failure, setFailure] = useState<DevComputeFailure | null>(null);

  useEffect(() => {
    if (!DEV_TOOLS_BUILD) return;
    const onFailure = (e: Event) => setFailure((e as CustomEvent<DevComputeFailure>).detail);
    window.addEventListener(DEV_COMPUTE_FAILED_EVENT, onFailure);
    return () => window.removeEventListener(DEV_COMPUTE_FAILED_EVENT, onFailure);
  }, []);

  if (!failure) return null;
  return <FailureCard failure={failure} onClose={() => setFailure(null)} />;
}

function FailureCard({ failure, onClose }: { failure: DevComputeFailure; onClose: () => void }) {
  const wallet = useWallet();
  const { refresh } = wallet;
  const upgrade = useUpgradeModal();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A failed job's charge never reaches the client (only a finished job
  // reports its balance), so re-read it for an honest count.
  useEffect(() => {
    void refresh();
  }, [failure, refresh]);

  if (!wallet.devTools || wallet.evalPoolRemaining === null) return null;

  async function reset() {
    setPending(true);
    setError(null);
    try {
      await resetDevCredits();
      await refresh();
      // A refused solve opened the hard paywall; with credits back it is moot.
      upgrade.close();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  }

  return createPortal(
    <div
      role="alertdialog"
      aria-labelledby="dev-compute-failure-title"
      data-testid="dev-compute-failure"
      // Above the upgrade modal (z-100) and the account menu (z-1000).
      className="fixed bottom-4 right-4 z-[1100] w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-[var(--app-border)] bg-[var(--app-raised)] p-4 shadow-2xl shadow-black/40"
    >
      <p className="ed-label">
        Dev tools
      </p>
      <p id="dev-compute-failure-title" className="mt-1 text-[13px] font-semibold text-[var(--rt-ink)]">
        {failure.reason === 'exhausted' ? 'Out of free optimizer runs' : 'Compute failed'}
      </p>
      <p className="mt-1 text-[12px] text-[var(--rt-ink-dim)]">{failure.message}</p>
      <p data-testid="dev-compute-failure-credits" className="mt-2 text-[12px] text-[var(--rt-ink-faint)]">
        Optimizer runs: {wallet.evalPoolRemaining} of {wallet.evalPoolTotal} left
      </p>
      {error && <p className="mt-1.5 text-[11px] text-[var(--rt-danger)]">{error}</p>}
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Dismiss
        </Button>
        <Button onClick={reset} disabled={pending} data-testid="dev-compute-failure-reset">
          Reset runs to {wallet.evalPoolTotal}
        </Button>
      </div>
    </div>,
    document.body,
  );
}
